import type {
  ConnectionNeeded,
  ConnectionTargetReference,
  CoveredZap,
} from "@zapier/bff";

/**
 * The subset of implementation meta the modal needs. Looked up per app
 * (versionless `selected_api`) to render a row's name/icon and to decide
 * whether a variant is deprecated.
 */
export interface ConnectionAppMeta {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  isDeprecated?: boolean;
  authType?: string | null;
  hasUpfrontFields?: boolean;
  images?: { url64X64?: string | null } | null;
}

/** One copied-asset connection requirement for a single `selected_api`. */
export interface ConnectionGroupEntry {
  selectedApi: string;
  targets: ConnectionTargetReference[];
}

/**
 * Apps the copied Zaps need connected, grouped by auth-compatibility key so a
 * single chosen connection only fans out to steps it is actually valid for.
 */
export interface AppConnectionGroup {
  /** Auth-compatibility key ({@link compatKey}); row id + wired-state key. */
  key: string;
  /** Representative versioned `selected_api` used to list/wire connections. */
  selectedApi: string;
  /** Meta to display + connect with (looked up by versionless name). */
  displayMeta?: ConnectionAppMeta;
  entries: ConnectionGroupEntry[];
  /** Distinct copied Zaps across the group's entries. */
  zapCount: number;
  /**
   * Pre-created credential-less placeholder auth id (CAA placeholder flow).
   * When set, the row is a placeholder row: it shows the placeholder title +
   * "Created for you", and "Connect" reconnects this auth in place (zsl-api
   * already wired it). Absent for legacy rows.
   */
  placeholderAuthId?: number;
  /** Server-generated placeholder title (the row's primary label). */
  placeholderTitle?: string;
  /** Copied Zaps (id + title) this row covers — drives the Zaps accordion. */
  coveredZaps?: CoveredZap[];
}

/** Versionless app id from a (possibly versioned) `selected_api`. */
export const versionless = (selectedApi: string) =>
  selectedApi.split("@")[0] ?? "";

/**
 * Auth-compatibility key for a `selected_api`: versionless name + major version
 * (`SlackCLIAPI@1.4.2` -> `SlackCLIAPI@1`); versionless apps unchanged. Mirrors
 * the platform's `get_compatible_selected_apis` and zsl-api's
 * `selectedApiCompatKey`, so incompatible variants (e.g. different majors that
 * share a slug) get separate rows.
 */
export const compatKey = (selectedApi: string): string => {
  const [name, version] = selectedApi.split("@");
  if (!version) return name ?? "";
  const major = version.split(".")[0] ?? "";
  return major ? `${name}@${major}` : (name ?? "");
};

/**
 * Tag to disambiguate two rows with the same app name: `v{major}` when the
 * compat key is versioned, else the versionless api id. Only shown on a
 * collision (see `ConnectAppsModal`).
 */
export const groupVersionTag = (
  group: Pick<AppConnectionGroup, "key" | "selectedApi">,
): string => {
  const major = group.key.split("@")[1];
  return major ? `v${major}` : versionless(group.selectedApi);
};

/**
 * Group `connectionsNeeded` by {@link compatKey} — one row per compatibility
 * set, keeping its per-`selected_api` entries for fan-out wiring.
 */
export function groupConnectionsByCompatKey(
  connectionsNeeded: ConnectionNeeded[],
  metaByApi: Map<string, ConnectionAppMeta>,
): AppConnectionGroup[] {
  const byKey = new Map<string, AppConnectionGroup>();
  const order: string[] = [];

  for (const { selectedApi, targets } of connectionsNeeded) {
    const key = compatKey(selectedApi);

    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        selectedApi,
        displayMeta: metaByApi.get(versionless(selectedApi)),
        entries: [],
        zapCount: 0,
      };
      byKey.set(key, group);
      order.push(key);
    }
    group.entries.push({ selectedApi, targets });
  }

  return order.map((key) => {
    const group = byKey.get(key)!;
    const zapIds = new Set<string>();
    for (const entry of group.entries) {
      for (const target of entry.targets) zapIds.add(target.id);
    }
    return { ...group, zapCount: zapIds.size };
  });
}

/**
 * Build the modal's rows from `connectionsNeeded`, handling both flows:
 *
 * - **Placeholder entries** (have a `placeholder`): one row each, keyed by
 *   the placeholder id. zsl-api already emits one entry per SOURCE auth, so the
 *   same app can legitimately appear in several rows — they must NOT be merged.
 *   The placeholder title is the row label; `coveredZaps` drives the accordion.
 * - **Legacy entries** (no placeholder): grouped by {@link compatKey} exactly as
 *   before (one row per compatibility set, with fan-out wiring), so the existing
 *   behavior is unchanged when the placeholder flow isn't active.
 *
 * Placeholder rows render first, then legacy rows, each preserving input order.
 */
export function buildConnectionRows(
  connectionsNeeded: ConnectionNeeded[],
  metaByApi: Map<string, ConnectionAppMeta>,
): AppConnectionGroup[] {
  const placeholderRows: AppConnectionGroup[] = [];
  // Fallback rows: placeholder create/wire failed for the entry (no
  // `placeholder`), but zsl-api still reported its covered Zaps. Kept as
  // one row per source auth (NOT merged) so the chosen/new auth wires onto
  // exactly that entry's steps and the row keeps its own Zaps accordion.
  const fallbackRows: AppConnectionGroup[] = [];
  // True legacy: neither placeholder nor covered Zaps (placeholder flow off).
  const legacyEntries: ConnectionNeeded[] = [];

  for (const [index, entry] of connectionsNeeded.entries()) {
    if (entry.placeholder != undefined) {
      const zapIds = new Set(entry.targets.map((t) => t.id));
      placeholderRows.push({
        key: `ph:${entry.placeholder.id}`,
        selectedApi: entry.selectedApi,
        displayMeta: metaByApi.get(versionless(entry.selectedApi)),
        entries: [{ selectedApi: entry.selectedApi, targets: entry.targets }],
        zapCount: entry.coveredZaps?.length ?? zapIds.size,
        placeholderAuthId: entry.placeholder.id,
        placeholderTitle: entry.placeholder.title,
        coveredZaps: entry.coveredZaps,
      });
      continue;
    }

    if (entry.coveredZaps?.length) {
      // Key on the entry's index (stable — `connectionsNeeded` order is fixed
      // from the response) so two fallback entries for the same app covering
      // the same Zap can't collide on a target-id-derived key.
      fallbackRows.push({
        key: `fb:${index}:${entry.selectedApi}`,
        selectedApi: entry.selectedApi,
        displayMeta: metaByApi.get(versionless(entry.selectedApi)),
        entries: [{ selectedApi: entry.selectedApi, targets: entry.targets }],
        zapCount: entry.coveredZaps.length,
        coveredZaps: entry.coveredZaps,
      });
      continue;
    }

    legacyEntries.push(entry);
  }

  return [
    ...placeholderRows,
    ...fallbackRows,
    ...groupConnectionsByCompatKey(legacyEntries, metaByApi),
  ];
}
